import { toast } from 'sonner'
import { useEffect, useState } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { api } from '@/lib/api'
import { EmptyState } from '@/components/shared/EmptyState'
import { MessageBody } from '@/components/shared/MessageBody'
import type { InboundMessage, Mailbox } from '@/types'
import {
  Inbox as InboxIcon,
  MailOpen,
  Mail,
  Loader2,
  Archive,
  Paperclip,
  X,
  ChevronLeft,
  ChevronRight,
  Search,
  MessageSquare,
  RotateCcw,
  Filter,
} from 'lucide-react'

interface MessageDetail extends InboundMessage {
  from?: { name?: string | null; email: string }
  to?: string[]
  cc?: string[]
  text_body?: string | null
  html_body?: string | null
  attachments?: Array<{ filename: string; content_type: string; size: number; url: string }>
}

export function Inbox() {
  const [messages, setMessages] = useState<InboundMessage[]>([])
  const [mailboxes, setMailboxes] = useState<Mailbox[]>([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [selectedMessage, setSelectedMessage] = useState<MessageDetail | null>(null)
  const [messageLoading, setMessageLoading] = useState(false)
  const [detailOpen, setDetailOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [searching, setSearching] = useState(false)
  const [mailboxFilter, setMailboxFilter] = useState('all')
  const [readFilter, setReadFilter] = useState('all')
  const [threadFilter, setThreadFilter] = useState<string | null>(null)

  const fetchMessages = async (pageNum = page) => {
    setLoading(true)
    try {
      const query = searchQuery.trim()
      let res
      if (query) {
        // CHECKPOINT: PRD-05 FR-5.6.1 — Full-text search via /inbound/search.
        const params = new URLSearchParams()
        params.set('q', query)
        params.set('page', String(pageNum))
        params.set('per_page', '20')
        if (mailboxFilter && mailboxFilter !== 'all') params.set('mailbox', mailboxFilter)
        res = await api.get(`/inbound/search?${params.toString()}`)
      } else {
        const params = new URLSearchParams()
        params.set('page', String(pageNum))
        params.set('per_page', '20')
        if (mailboxFilter && mailboxFilter !== 'all') params.set('mailbox', mailboxFilter)
        if (readFilter === 'unread') params.set('unread', 'true')
        if (readFilter === 'read') params.set('unread', 'false')
        if (threadFilter) params.set('thread_id', threadFilter)

        res = await api.get(`/inbound/messages?${params.toString()}`)
      }
      setMessages(res.data.messages || [])
      setTotal(res.data.pagination?.total || 0)
      setTotalPages(res.data.pagination?.total_pages || 1)
    } catch {
      toast.error('Failed to load messages')
    } finally {
      setLoading(false)
    }
  }

  const fetchMailboxes = async () => {
    try {
      const res = await api.get('/mailboxes')
      setMailboxes(res.data.mailboxes || [])
    } catch {
      // silent
    }
  }

  const handleSearch = async () => {
    setSearching(true)
    setPage(1)
    try {
      await fetchMessages(1)
    } finally {
      setSearching(false)
    }
  }

  useEffect(() => {
    fetchMailboxes()
  }, [])

  useEffect(() => {
    fetchMessages()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, mailboxFilter, readFilter, threadFilter])

  const markRead = async (id: string, read: boolean) => {
    try {
      await api.patch(`/inbound/messages/${id}`, { read })
      setMessages((prev) =>
        prev.map((m) => (m.id === id ? { ...m, is_read: read } : m))
      )
      if (selectedMessage && selectedMessage.id === id) {
        setSelectedMessage({ ...selectedMessage, is_read: read })
      }
    } catch {
      toast.error('Failed to update message')
    }
  }

  const archiveMessage = async (id: string) => {
    try {
      await api.patch(`/inbound/messages/${id}`, { archived: true })
      setDetailOpen(false)
      fetchMessages()
    } catch {
      toast.error('Failed to archive message')
    }
  }

  const openMessage = async (msg: InboundMessage) => {
    setDetailOpen(true)
    setMessageLoading(true)
    try {
      const res = await api.get(`/inbound/messages/${msg.id}`)
      setSelectedMessage(res.data)
      if (!msg.is_read) {
        await markRead(msg.id, true)
      }
    } catch {
      toast.error('Failed to load message')
      setSelectedMessage(null)
    } finally {
      setMessageLoading(false)
    }
  }

  const viewThread = (threadId: string) => {
    setThreadFilter(threadId)
    setPage(1)
  }

  const clearThreadFilter = () => {
    setThreadFilter(null)
    setPage(1)
  }

  if (loading && messages.length === 0) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-8 w-32 bg-accent rounded" />
        <div className="h-64 bg-accent rounded-xl" />
      </div>
    )
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Inbox</h1>
          <p className="text-sm text-muted-foreground mt-1">Recent inbound emails</p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search inbox..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="h-9 w-full sm:w-56 pl-9"
            />
          </div>
          <Select value={mailboxFilter} onValueChange={(v) => { setMailboxFilter(v); setPage(1) }}>
            <SelectTrigger className="h-9 w-full sm:w-40">
              <Filter className="h-3.5 w-3.5 mr-1.5" />
              <SelectValue placeholder="Mailbox" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All mailboxes</SelectItem>
              {mailboxes.map((mb) => (
                <SelectItem key={mb.id} value={mb.email}>{mb.email}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={readFilter} onValueChange={(v) => { setReadFilter(v); setPage(1) }}>
            <SelectTrigger className="h-9 w-full sm:w-36">
              <SelectValue placeholder="Read status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="unread">Unread</SelectItem>
              <SelectItem value="read">Read</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={handleSearch} disabled={searching}>
            {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Search'}
          </Button>
        </div>
      </div>

      {threadFilter && (
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="gap-1.5">
            Thread view
            <button onClick={clearThreadFilter} className="hover:text-destructive">
              <X className="h-3 w-3" />
            </button>
          </Badge>
          <Button variant="ghost" size="sm" onClick={clearThreadFilter}>
            <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
            Clear
          </Button>
        </div>
      )}

      {messages.length === 0 ? (
        <EmptyState
          title="No emails yet"
          description="Emails sent to your domains will appear here."
          icon={<InboxIcon className="h-5 w-5" />}
        />
      ) : (
        <div className="grid gap-2">
          {messages.map((msg) => (
            <Card
              key={msg.id}
              className={`hover:bg-accent/20 transition-colors cursor-pointer group ${
                !msg.is_read ? 'border-l-2 border-l-blue-400' : ''
              }`}
              onClick={() => openMessage(msg)}
            >
              <CardContent className="p-3 flex items-center gap-3">
                <button
                  onClick={(e) => { e.stopPropagation(); markRead(msg.id, !msg.is_read); }}
                  className="shrink-0 rounded-full p-1 hover:bg-accent"
                  title={msg.is_read ? 'Mark unread' : 'Mark read'}
                >
                  {msg.is_read ? (
                    <MailOpen className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Mail className="h-4 w-4 text-blue-400" />
                  )}
                </button>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className={`text-sm truncate ${!msg.is_read ? 'font-semibold' : ''}`}>
                      {msg.from_addr?.name || msg.from_addr?.email}
                    </p>
                    <span className="text-xs text-muted-foreground hidden sm:inline">
                      {msg.from_addr?.email}
                    </span>
                    {msg.thread_id && (
                      <Badge
                        variant="outline"
                        className="h-5 text-[10px] cursor-pointer hover:bg-accent"
                        onClick={(e) => { e.stopPropagation(); viewThread(msg.thread_id!); }}
                      >
                        <MessageSquare className="h-3 w-3 mr-1" />
                        Thread
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{msg.subject || '(no subject)'}</p>
                  {msg.body_preview && (
                    <p className="text-xs text-muted-foreground/60 truncate mt-0.5">{msg.body_preview}</p>
                  )}
                </div>
                <div className="shrink-0 text-right">
                  <span className="text-[11px] text-muted-foreground">
                    {new Date(msg.email_date).toLocaleDateString()}
                  </span>
                  {msg.has_attachments && (
                    <div className="flex items-center justify-end gap-1 text-[10px] text-muted-foreground mt-0.5">
                      <Paperclip className="h-3 w-3" />
                      {msg.attachment_count}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {messages.length > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">{total} total</p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm">Page {page}</span>
            <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Message Reader Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0 gap-0">
          {messageLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-violet-400" />
            </div>
          ) : selectedMessage ? (
            <>
              <DialogHeader className="px-6 py-4 border-b">
                <div className="flex items-start justify-between gap-4">
                  <DialogTitle className="text-base font-semibold pr-4 leading-snug">
                    {selectedMessage.subject || '(no subject)'}
                  </DialogTitle>
                  <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => setDetailOpen(false)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </DialogHeader>

              <div className="px-6 py-4 space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 text-sm">
                  <div className="space-y-1 min-w-0">
                    <p>
                      <span className="text-muted-foreground">From:</span>{' '}
                      {selectedMessage.from?.name && <span>{selectedMessage.from.name} </span>}
                      <span className="text-muted-foreground">&lt;{selectedMessage.from?.email}&gt;</span>
                    </p>
                    {selectedMessage.to && selectedMessage.to.length > 0 && (
                      <p className="truncate">
                        <span className="text-muted-foreground">To:</span>{' '}
                        {selectedMessage.to.map((r: any) => typeof r === 'string' ? r : r.email || r).join(', ')}
                      </p>
                    )}
                    {selectedMessage.cc && selectedMessage.cc.length > 0 && (
                      <p className="truncate">
                        <span className="text-muted-foreground">Cc:</span>{' '}
                        {selectedMessage.cc.map((r: any) => typeof r === 'string' ? r : r.email || r).join(', ')}
                      </p>
                    )}
                    <p>
                      <span className="text-muted-foreground">Mailbox:</span>{' '}
                      {selectedMessage.mailbox}
                    </p>
                  </div>
                  <span className="text-xs text-muted-foreground shrink-0">
                    {selectedMessage.email_date && new Date(selectedMessage.email_date).toLocaleString()}
                  </span>
                </div>

                {selectedMessage.attachments && selectedMessage.attachments.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-muted-foreground">Attachments:</span>
                    {selectedMessage.attachments.map((att, idx) => (
                      <a
                        key={idx}
                        href={att.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs bg-accent/50 rounded px-2 py-1 hover:bg-accent"
                      >
                        <Paperclip className="h-3 w-3" />
                        {att.filename}
                      </a>
                    ))}
                  </div>
                )}

                <div className="border-t pt-4">
                  <MessageBody
                    html={selectedMessage.html_body}
                    text={selectedMessage.text_body}
                    preview={selectedMessage.body_preview}
                    attachments={selectedMessage.attachments}
                  />
                </div>

                <div className="flex items-center justify-end gap-2 pt-4 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => markRead(selectedMessage.id, !selectedMessage.is_read)}
                  >
                    {selectedMessage.is_read ? (
                      <>
                        <Mail className="mr-2 h-4 w-4" /> Mark unread
                      </>
                    ) : (
                      <>
                        <MailOpen className="mr-2 h-4 w-4" /> Mark read
                      </>
                    )}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => archiveMessage(selectedMessage.id)}>
                    <Archive className="mr-2 h-4 w-4" />
                    Archive
                  </Button>
                </div>
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  )
}
